import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import illustration from "../../assets/signin-illustration.webp";
import { signInUser } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { toast, Slide } from "react-toastify";

const SignIn = () => {
  const [email, setEmail] = useState(() => localStorage.getItem("lastEmail") || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { login, user, token } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (user && token) {
      navigate("/dashboard/vehicles", { replace: true });
    }
  }, [user, token, navigate]);

  useEffect(() => {
    const emailInput = document.querySelector("input[type='email']");
    if (emailInput && emailInput.value && email === "") {
      setEmail(emailInput.value);
    }
  }, [email]);

  const validate = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required!";
    else if (!/^\S+@\S+\.\S+$/.test(email)) newErrors.email = "Invalid email format!";
    if (!password) newErrors.password = "Password is required!";
    else if (password.length < 6) newErrors.password = "Password must be at least 6 characters!";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!validate()) {
      toast.error("Please fix the validation errors.", {
        transition: Slide,
        position: "top-center",
        autoClose: 3000
      });
      return;
    }

    setLoading(true);
    try {
      const data = await signInUser(email, password);

      login({
        user: { email },
        token: data.token,
        expiresIn: data.expiresIn || 3600
      });

      if (rememberMe) {
        localStorage.setItem("lastEmail", email);
      } else {
        localStorage.removeItem("lastEmail");
      }

      toast.success("Signed in successfully! Redirecting...", {
        transition: Slide,
        position: "top-center",
        autoClose: 2000,
        className: '!z-[100] !fixed !top-16 !left-1/2 !-translate-x-1/2',
        style: { zIndex: 9999 }
      });

      setTimeout(() => {
        navigate("/dashboard/vehicles", { replace: true });
      }, 2100);

    } catch (err) {
      const message = err.response?.data?.message || "Sign in failed.";
      toast.error(message, {
        transition: Slide,
        position: "top-center",
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white dark:bg-gray-900">
      {/* Left Panel */}
      <div className="w-full md:w-1/2 flex flex-col justify-center items-center bg-gradient-to-br from-white to-gray-100 p-8 dark:from-gray-800 dark:to-gray-900">
        <h2 className="text-3xl font-bold mb-4">Hi, Welcome back</h2>
        <p className="text-gray-500 mb-6 text-center">More effectively with optimized workflows.</p>
        <img src={illustration} alt="Workflow" className="max-w-xs md:max-w-md" />
      </div>

      {/* Right Form Panel */}
      <div className="w-full md:w-1/2 flex justify-center items-center p-8">
        <form noValidate onSubmit={handleSubmit} className="w-full max-w-md space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sign in to your account</h2>
            <Link to="/help" className="text-sm text-gray-500 hover:underline">Need help?</Link>
          </div>

          <p className="text-sm text-gray-500 dark:text-gray-300">
            Don’t have an account? <Link to="/auth/signup" className="text-orange-500 font-medium">Get started</Link>
          </p>

          <div>
            <label className="block text-sm mb-1 font-medium text-gray-700 dark:text-gray-300">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-4 py-2 border rounded focus:outline-none dark:bg-gray-800 dark:text-white ${errors.email ? "border-red-500" : ""}`}
              placeholder="you@example.com"
            />
            {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <div className="flex justify-between items-center">
              <label className="block text-sm mb-1 font-medium text-gray-700 dark:text-gray-300">Password</label>
              <Link to="/auth/resetpassword" className="text-sm text-orange-500 hover:underline">Forgot password?</Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-2 pr-10 border rounded focus:outline-none dark:bg-gray-800 dark:text-white ${errors.password ? "border-red-500" : ""}`}
                placeholder="6+ characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute top-1/2 right-3 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-white"
                tabIndex={-1}
              >
                {showPassword ? <span>&#128065;</span> : <span>&#128583;</span>}
              </button>
            </div>
            {errors.password && <p className="text-sm text-red-500 mt-1">{errors.password}</p>}
          </div>

          <div className="flex items-center space-x-2">
            <input
              id="rememberMe"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="form-checkbox rounded text-orange-500"
            />
            <label htmlFor="rememberMe" className="text-sm text-gray-700 dark:text-gray-300">
              Remember this email on this device
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded font-semibold disabled:opacity-50"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>

          <div className="flex items-center justify-center space-x-4 pt-4">
            <span className="text-gray-400 text-sm">OR</span>
          </div>

          <div className="flex justify-center gap-4 text-2xl">
            <button type="button" className="hover:scale-110 transition"><i className="fab fa-google"></i></button>
            <button type="button" className="hover:scale-110 transition"><i className="fab fa-github"></i></button>
            <button type="button" className="hover:scale-110 transition"><i className="fab fa-x-twitter"></i></button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;
